// components/StickySidebar.js
export function initStickySidebar({
  minWidth = 768,
  containerId = 'naluFormBodyStep3',
  sidebarId = 'naluStickySidebar',
  top = 0,
  bottom = 64,
} = {}) {
  const mq = window.matchMedia(`(min-width:${minWidth}px)`);

  const container = document.getElementById(containerId);
  const sidebar = document.getElementById(sidebarId);
  if (!container || !sidebar) return;

  // If we already attached a media listener before, don't duplicate it
  if (sidebar._stickyMqHandler) {
    // But still respond to current state (useful if Step 3 re-rendered)
    mq.matches ? setupDesktop() : destroyStickySidebar(sidebar);
    return;
  }

  const onMediaChange = () => {
    if (mq.matches) setupDesktop();
    else destroyStickySidebar(sidebar);
  };

  // Store so we can avoid re-adding on subsequent init calls
  sidebar._stickyMqHandler = onMediaChange;

  // Listen to breakpoint changes
  if (mq.addEventListener) mq.addEventListener('change', onMediaChange);
  else mq.addListener(onMediaChange); // Safari fallback

  // Initial run
  onMediaChange();

  function setupDesktop() {
    // teardown first (prevents duplicates + stale closures)
    destroyStickySidebar(sidebar);

    // Helper: always get current inner (Step 3 can re-render)
    function getInner() {
      let inner = sidebar.querySelector('.nalu-sticky-inner');
      if (!inner) {
        inner = document.createElement('div');
        inner.className = 'nalu-sticky-inner';
        while (sidebar.firstChild) inner.appendChild(sidebar.firstChild);
        sidebar.appendChild(inner);
      }
      return inner;
    }

    let lastScrollTop = container.scrollTop || 0;
    let offset = 0;
    let maxOffset = 0;
    let enabled = false;

    // rAF scheduling to avoid “too-early” recalc on iPad orientation flips / image resize
    let recalcScheduled = false;
    const scheduleRecalc = () => {
      if (!mq.matches) return;
      if (recalcScheduled) return;
      recalcScheduled = true;
      requestAnimationFrame(() => {
        recalcScheduled = false;
        recalc();
      });
    };

    function bindSidebarImageLoadRecalc() {
      const inner = getInner();
      const imgs = inner.querySelectorAll('img');
      imgs.forEach((img) => {
        // If already loaded, nothing to do
        if (img.complete) return;
        img.addEventListener('load', scheduleRecalc, { once: true });
        img.addEventListener('error', scheduleRecalc, { once: true });
      });
    }

    function recalc() {
      if (!mq.matches) return;
      if (container.clientHeight === 0) return;

      const inner = getInner();

      // If layout has changed (orientation / resize), reset scroll tracking
      lastScrollTop = container.scrollTop || 0;

      const available = Math.max(0, container.clientHeight - top - bottom);
      const contentHeight = inner.scrollHeight;

      sidebar.style.position = 'sticky';
      sidebar.style.top = `${top}px`;
      sidebar.style.alignSelf = 'flex-start';

      enabled = contentHeight > available + 1;

      if (!enabled) {
        sidebar.style.height = 'auto';
        sidebar.style.overflow = 'visible';
        inner.style.transform = '';
        offset = 0;
        maxOffset = 0;
        return;
      }

      sidebar.style.height = `${available}px`;

      maxOffset = contentHeight - available;
      offset = Math.min(Math.max(0, offset), maxOffset);
      inner.style.transform = `translate3d(0, ${-offset}px, 0)`;

      // Images may still be resizing after orientation flip; ensure we listen
      bindSidebarImageLoadRecalc();
    }

    function update() {
      if (!enabled) {
        lastScrollTop = container.scrollTop;
        return;
      }

      const inner = getInner();
      const current = container.scrollTop;
      const delta = current - lastScrollTop;
      if (delta === 0) return;

      offset = Math.min(maxOffset, Math.max(0, offset + delta));
      inner.style.transform = `translate3d(0, ${-offset}px, 0)`;
      lastScrollTop = current;
    }

    // rAF throttled scroll handler
    let ticking = false;
    const onScroll = () => {
      if (!mq.matches) return;
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        update();
      });
    };

    // Resize/orientation => schedule recalculation (let Safari settle)
    const onResize = () => {
      if (!mq.matches) return;
      scheduleRecalc();
      // extra passes catch delayed iPad layout updates
      setTimeout(scheduleRecalc, 120);
      setTimeout(scheduleRecalc, 250);
    };

    const onOrientation = () => {
      if (!mq.matches) return;
      scheduleRecalc();
      setTimeout(scheduleRecalc, 120);
      setTimeout(scheduleRecalc, 250);
    };

    // ResizeObserver catches inner size changes caused by image ratio/layout shifts
    const ro = new ResizeObserver(() => {
      if (!mq.matches) return;
      scheduleRecalc();
    });

    // Bind listeners
    container.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onOrientation);

    // Observe size changes
    ro.observe(container);
    ro.observe(getInner());

    // Store teardown refs
    sidebar._stickyState = { onScroll, onResize, onOrientation, container, ro };

    // First run (plus “late” image load listeners)
    bindSidebarImageLoadRecalc();
    scheduleRecalc();
  }
}

function destroyStickySidebar(sidebar) {
  const st = sidebar?._stickyState;
  if (st) {
    try {
      st.container?.removeEventListener('scroll', st.onScroll);
      window.removeEventListener('resize', st.onResize);
      window.removeEventListener('orientationchange', st.onOrientation);
      st.ro?.disconnect();
    } catch {}
    delete sidebar._stickyState;
  }

  // Reset styles so layout is clean
  const inner = sidebar?.querySelector?.('.nalu-sticky-inner');
  if (sidebar) {
    sidebar.style.position = '';
    sidebar.style.top = '';
    sidebar.style.height = '';
    sidebar.style.overflow = '';
    sidebar.style.alignSelf = '';
  }
  if (inner) inner.style.transform = '';
}
